import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { F, DUR } from '../../styles/theme';
import { Icon, Icons } from './Icons';

const RED = '#E4544A';

/**
 * Modernes Bottom-Sheet zum Auswählen einer Option (Sprache, Währung, Jahr …).
 * Öffnet von unten (über der Nav) mit Spring-Motion, Drag-to-dismiss, neutralem
 * Highlight + rotem Check für die Auswahl (kein rot-getönter Container).
 */
export default function SelectSheet({ open, title, options, value, onSelect, onClose }) {
  const { T, isDark } = useTheme();
  const sel = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(12,12,15,0.05)';
  if (typeof document === 'undefined') return null;
  // Per Portal an document.body: sonst rendert das Sheet INNERHALB von <IonContent>
  // (eigener Stacking-Context) und landet trotz hohem z-index HINTER der Bottom-Nav.
  return createPortal(
    <AnimatePresence>
      {open && (
          <motion.div
            key="ss-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DUR.base / 1000 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
              zIndex: 1000,
            }}
          />
      )}
      {open && (
          <motion.div
            key="ss-sheet"
            initial={{ y: '112%' }}
            animate={{ y: 0 }}
            exit={{ y: '112%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 40, mass: 0.9 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_e, info) => { if (info.offset.y > 90 || info.velocity.y > 600) onClose(); }}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, margin: '0 auto',
              width: '100%', maxWidth: 480,
              background: T.surface,
              borderTop: `1px solid ${T.line}`,
              borderRadius: '24px 24px 0 0',
              padding: `14px 16px calc(env(safe-area-inset-bottom, 0px) + 22px)`,
              zIndex: 1001, color: T.text,
              boxShadow: '0 -14px 44px rgba(0,0,0,0.45)',
            }}
          >
            {/* Grabber */}
            <div style={{ width: 40, height: 4, borderRadius: 999, background: T.line, margin: '0 auto 16px', cursor: 'grab' }} />
            {title && (
              <div style={{
                fontFamily: F.display, fontSize: 17, fontWeight: 700,
                letterSpacing: '-0.02em', color: T.text, padding: '0 4px 14px',
              }}>{title}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {options.map((opt) => {
                const selected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => { onSelect(opt.value); onClose(); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, width: '100%', textAlign: 'left',
                      padding: '15px 16px', borderRadius: 14,
                      background: selected ? sel : 'transparent',
                      border: `1px solid ${selected ? T.line : 'transparent'}`,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                      transition: `background ${DUR.fast}ms ease`,
                    }}
                  >
                    <span style={{
                      fontFamily: F.family, fontSize: 15,
                      fontWeight: selected ? 700 : 500,
                      letterSpacing: '-0.01em',
                      color: selected ? RED : T.text,
                    }}>{opt.label}</span>
                    {selected && <Icon d={Icons.check} size={17} stroke={RED} sw={2.6} />}
                  </button>
                );
              })}
            </div>
          </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
